import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { canReplyToMessages } from '../../utils/membershipUtils';
import { API_BASE_URL } from '../../config/api';
import './SupplierInquiriesPage.css';

const SupplierInquiriesPage = () => {
  const { user } = useAuth();
  const { planName } = useSubscription();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyModal, setReplyModal] = useState({ open: false, inquiry: null });
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Get supplier/seller ID from user
  const getSupplierId = useCallback(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.sellerId || user?.sellerProfileId || storedUser?.sellerId || storedUser?.sellerProfileId || user?.id || storedUser?.id;
  }, [user]);

  // Fetch inquiries from API
  const fetchInquiries = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    
    try {
      const supplierId = getSupplierId();
      const token = localStorage.getItem('token');
      
      if (!supplierId) {
        setError('Unable to identify supplier account');
        setLoading(false);
        return;
      }

      // Fetch RFQs targeted to this seller from the RFQ endpoint
      // Use userId parameter which finds seller profiles linked to this user
      const response = await fetch(`${API_BASE_URL}/rfq?userId=${supplierId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Transform RFQ data to match the expected format
        const rfqs = data.rfqs || data || [];
        const transformedInquiries = rfqs.map(rfq => ({
          id: rfq.id,
          senderName: rfq.fullName || rfq.companyName || 'Unknown',
          senderCompany: rfq.companyName || '',
          senderEmail: rfq.email || '',
          senderPhone: rfq.phoneNumber || '',
          senderType: 'buyer',
          country: rfq.country || '',
          subject: rfq.category || 'Product Inquiry',
          message: rfq.details || '',
          quantity: rfq.quantity,
          unit: rfq.unit,
          offerPrice: rfq.offerPrice,
          status: rfq.status || 'new',
          createdAt: rfq.createdAt,
          isRead: rfq.status !== 'new',
          isReplied: rfq.status === 'replied'
        }));
        setInquiries(transformedInquiries);
      } else {
        setError(data.error || 'Failed to fetch inquiries');
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      if (!silent) {
        setError('Failed to load inquiries. Please try again.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [getSupplierId]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  // Live polling - refresh inquiries every 10 seconds (silent)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchInquiries(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchInquiries]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Get sender type badge class
  const getSenderTypeClass = (senderType) => {
    if (senderType === 'admin') return 'admin';
    if (senderType === 'buyer') return 'buyer';
    return '';
  };

  // Handle reply button click
  const handleReplyClick = (inquiry) => {
    // Check if user can reply based on plan
    // Admin bypass - admin can do everything
    const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
    if (!isAdminUser && !canReplyToMessages(planName)) {
      setShowUpgradeModal(true);
      return;
    }
    setReplyModal({ open: true, inquiry });
    setReplyMessage('');
  };

  // Handle reply submit
  const handleReplySubmit = async () => {
    if (!replyMessage.trim() || !replyModal.inquiry) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const supplierId = getSupplierId();
      
      // Send reply via API
      const response = await fetch(`${API_BASE_URL}/rfq/${replyModal.inquiry.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: replyMessage,
          sellerId: supplierId,
          recipientEmail: replyModal.inquiry.senderEmail
        })
      });

      if (response.ok) {
        alert('Reply sent successfully!');
        // Mark as replied after sending
        setInquiries(prev => 
          prev.map(inq => 
            inq.id === replyModal.inquiry.id ? { ...inq, isRead: true, isReplied: true, status: 'replied' } : inq
          )
        );
        setReplyModal({ open: false, inquiry: null });
        setReplyMessage('');
      } else {
        const data = await response.json();
        // Check if it's a plan restriction error
        if (data.errorCode === 'MESSAGING_NOT_ALLOWED') {
          setReplyModal({ open: false, inquiry: null });
          setShowUpgradeModal(true);
        } else {
          alert(data.error || 'Failed to send reply. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Mark inquiry as read
  const handleMarkRead = async (inquiryId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Call API to mark as read
      const response = await fetch(`${API_BASE_URL}/rfq/${inquiryId}/read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        // Update local state
        setInquiries(prev => 
          prev.map(inq => 
            inq.id === inquiryId ? { ...inq, isRead: true } : inq
          )
        );
      } else {
        console.error('Failed to mark as read');
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  // Count new/unread inquiries
  const newCount = inquiries.filter(inq => !inq.isRead).length;

  return (
    <SellerDashboardLayout activeItem="inquiries">
      <div className="supplier-inquiries-page">
        <div className="supplier-inquiries-header">
          <h1>Inquiries Received</h1>
          <div className="inquiries-stats">
            <span className="stat-badge total">Total: {inquiries.length}</span>
            <span className="stat-badge new">New: {newCount}</span>
          </div>
        </div>

        {loading ? (
          <div className="inquiries-loading">
            <p>Loading inquiries...</p>
          </div>
        ) : error ? (
          <div className="inquiries-error">
            <p>{error}</p>
            <button onClick={fetchInquiries}>Try Again</button>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="inquiries-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3>No Inquiries Yet</h3>
            <p>When buyers or admins send you inquiries, they will appear here.</p>
          </div>
        ) : (
          <div className="inquiries-list">
            {inquiries.map((inquiry) => (
              <div key={inquiry.id} className={`inquiry-card ${!inquiry.isRead ? 'unread' : ''}`}>
                <div className="inquiry-header">
                  <div className="inquiry-sender">
                    <span className="sender-name">{inquiry.senderName || 'Unknown Sender'}</span>
                    <span className="sender-email">{inquiry.senderEmail}</span>
                    {inquiry.senderType && (
                      <span className={`sender-type ${getSenderTypeClass(inquiry.senderType)}`}>
                        {inquiry.senderType === 'admin' ? 'Admin' : inquiry.senderType === 'buyer' ? 'Buyer' : 'User'}
                      </span>
                    )}
                  </div>
                  <span className="inquiry-date">{formatDate(inquiry.createdAt)}</span>
                </div>
                
                {inquiry.subject && (
                  <div className="inquiry-subject">{inquiry.subject}</div>
                )}
                
                <div className="inquiry-message">{inquiry.message}</div>
                
                <div className="inquiry-actions">
                  {inquiry.isReplied ? (
                    <span className="status-badge replied">Replied</span>
                  ) : (
                    <>
                      {!inquiry.isRead && (
                        <button className="btn-mark-read" onClick={() => handleMarkRead(inquiry.id)}>
                          Mark as Read
                        </button>
                      )}
                      <button className="btn-reply" onClick={() => handleReplyClick(inquiry)}>
                        Reply
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reply Modal */}
        {replyModal.open && (
          <div className="reply-modal-overlay" onClick={() => setReplyModal({ open: false, inquiry: null })}>
            <div className="reply-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Reply to {replyModal.inquiry?.senderName || 'Sender'}</h3>
              <p className="reply-to-email">To: {replyModal.inquiry?.senderEmail || 'N/A'}</p>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your reply here..."
                autoFocus
              />
              <div className="reply-modal-actions">
                <button className="btn-cancel" onClick={() => setReplyModal({ open: false, inquiry: null })}>
                  Cancel
                </button>
                <button 
                  className="btn-send" 
                  onClick={handleReplySubmit}
                  disabled={!replyMessage.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="Reply to Inquiries"
          message="Basic plan users cannot reply to inquiries. Please upgrade to GCC Business or Global Enterprise plan to message buyers."
        />
      </div>
    </SellerDashboardLayout>
  );
};

export default SupplierInquiriesPage;
